import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TRANSLATIONS, LANGUAGE_OPTIONS } from '@/lib/translations';
import { settingsManager } from '@/lib/vatUtils';

const LanguageContext = createContext();

// Detect browser language
const detectBrowserLanguage = () => {
  const supportedLanguages = ['en', 'de', 'fr', 'it', 'es'];
  
  // Check multiple sources for language
  const sources = [
    navigator.language,
    navigator.userLanguage,
    ...navigator.languages || []
  ];
  
  for (const lang of sources) {
    if (lang) {
      const langCode = lang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
  }
  
  return 'en'; // Default fallback
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // First check saved preference
    const settings = settingsManager.get();
    if (settings.language) {
      return settings.language;
    }
    
    // Otherwise auto-detect from browser
    return detectBrowserLanguage();
  });

  const [isAutoDetected, setIsAutoDetected] = useState(() => {
    const settings = settingsManager.get();
    return !settings.language; // True if no saved preference
  });

  const t = (key) => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS['en'][key] || key;
  };

  // Announce language change to screen readers
  const announceLanguageChange = useCallback((langName) => {
    const announcement = document.getElementById('sr-announcements');
    if (announcement) {
      announcement.textContent = '';
      setTimeout(() => {
        announcement.textContent = `Language changed to ${langName}`;
      }, 100);
    }
  }, []);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    setIsAutoDetected(false);
    settingsManager.set({ language: lang });
    
    // Announce to screen readers
    const langName = LANGUAGE_OPTIONS.find(l => l.code === lang)?.name || lang;
    announceLanguageChange(langName);
  }, [announceLanguageChange]);

  // Update meta tags based on language
  const updateMetaTags = useCallback((lang) => {
    // Update title - use direct translation to avoid dependency on t
    const titles = {
      en: 'VAT Calculator Free | Add Remove VAT Online - vat.expert',
      de: 'MwSt Rechner Kostenlos | MwSt Online Berechnen - vat.expert',
      fr: 'Calculateur TVA Gratuit | Calcul TVA en Ligne - vat.expert',
      it: 'Calcolatore IVA Gratis | Calcolo IVA Online - vat.expert',
      es: 'Calculadora IVA Gratis | Calcular IVA Online - vat.expert'
    };
    document.title = titles[lang] || titles.en;
    
    // Update meta description
    const descriptions = {
      en: 'Free professional VAT calculator for 62 countries. Add VAT, remove VAT, scorporo. All EU rates.',
      de: 'Kostenloser MwSt Rechner für 62 Länder. MwSt hinzufügen, herausrechnen. Alle EU-Sätze.',
      fr: 'Calculateur TVA gratuit pour 62 pays. Ajouter TVA, calculer HT. Tous les taux EU.',
      it: 'Calcolatore IVA gratuito per 62 paesi. Aggiungi IVA, scorporo IVA. Tutte le aliquote EU.',
      es: 'Calculadora IVA gratuita para 62 países. Añadir IVA, quitar IVA. Todos los tipos EU.'
    };
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', descriptions[lang] || descriptions.en);
    }
    
    // Update Open Graph locale
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    const localeMap = {
      en: 'en_GB',
      de: 'de_DE',
      fr: 'fr_FR',
      it: 'it_IT',
      es: 'es_ES'
    };
    if (ogLocale) {
      ogLocale.setAttribute('content', localeMap[lang] || 'en_GB');
    }
  }, []);

  // Update document language and meta tags when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    updateMetaTags(language);
  }, [language, updateMetaTags]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage, 
      t, 
      LANGUAGE_OPTIONS,
      isAutoDetected,
      detectBrowserLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageContext;
